/**
 * HeaderSearchButton Container Component
 *
 * Handles search state, GraphQL queries, and navigation for header search.
 * @module components/HeaderSearchButton
 */

import { Href, usePathname, useRouter } from "expo-router";
import { Search } from "lucide-react-native";
import React, { useCallback, useRef, useState } from "react";

import HeaderSearchButtonView from "./HeaderSearchButtonView";

import { TextInput } from "@/components/atoms/TextInput";

import ManuallyAddPlayerModal from "@/components/organisms/ManuallyAddPlayerModal";
// design-system: button size snapped xl→lg (closed size scale)
import { Button } from "@/components/atoms/Button";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import { usePlayerNameOrTransfermarktSearch } from "@/hooks/usePlayerNameOrTransfermarktSearch";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { useTheme } from "@/providers/ThemeProvider";

/**
 * Container component for header search button
 * Handles search state, GraphQL queries, and player navigation
 */
const HeaderSearchButtonContainer = () => {
  const router = useRouter();
  const pathname = usePathname();
  const getExpandedUrl = useGetExpandedPlayerUrl();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const inputRef = useRef<React.ComponentRef<typeof TextInput>>(null);
  const isManualAddPlayerEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.MANUALLY_ADD_PLAYER) === true;

  const [isOpen, setIsOpen] = useState(false);
  const [isManualAddOpen, setIsManualAddOpen] = useState(false);

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Shared debounced name search + Transfermarkt URL lookup (SE-3599). The
  // hook clears its own search state after a URL resolves and navigates, so
  // onNavigated only closes the popover.
  const {
    searchText,
    results,
    isLoading,
    transfermarktNotFoundId,
    handleChangeText,
    resetSearch,
  } = usePlayerNameOrTransfermarktSearch({
    origin: { label: "Search", path: pathname, reopenSearch: true },
    onNavigated: closePopover,
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    resetSearch();
  }, [resetSearch]);

  const { pushBreadcrumb, clearBreadcrumbs } = useNavigationContext();

  const handleSelectPlayer = useCallback(
    (playerId: string, playerName?: string) => {
      const playerUrl = getExpandedUrl(`/players/${playerId}`) as string;

      clearBreadcrumbs();
      // Seed the origin as the CURRENT route (a real, reachable page) plus the
      // SE-5387 reopen flag — NOT the mobile-only "/search" tab, which renders
      // a blank page on desktop web (this header search is web-only). Mirrors
      // the global-search modal's origin seeding so the breadcrumb back button
      // returns to a valid route instead of a dead /search route.
      pushBreadcrumb({ label: "Search", path: pathname, reopenSearch: true });
      pushBreadcrumb({ label: playerName || "Player", path: playerUrl });

      handleClose();
      router.push(playerUrl as Href);
    },
    [
      handleClose,
      router,
      getExpandedUrl,
      pushBreadcrumb,
      clearBreadcrumbs,
      pathname,
    ]
  );

  const handleClearText = useCallback(() => {
    handleChangeText("");
  }, [handleChangeText]);

  const handleManualAddPlayer = useCallback(() => {
    setIsManualAddOpen(true);
  }, []);

  const handleManualAddClose = useCallback(() => {
    setIsManualAddOpen(false);
  }, []);

  const renderTrigger = useCallback(
    (triggerProps: object) => (
      <Button
        size="lg"
        variant="link"
        className="rounded-none px-3 py-2"
        accessible
        accessibilityLabel="Search players"
        accessibilityRole="button"
        icon={Search}
        {...triggerProps}
      />
    ),
    []
  );

  return (
    <>
      <HeaderSearchButtonView
        isOpen={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        searchText={searchText}
        onChangeText={handleChangeText}
        onClearText={handleClearText}
        results={results}
        isLoading={isLoading}
        transfermarktNotFoundId={transfermarktNotFoundId}
        onSelectPlayer={handleSelectPlayer}
        onManualAddPlayer={
          isManualAddPlayerEnabled ? handleManualAddPlayer : undefined
        }
        inputRef={inputRef}
        isDark={isDark}
        renderTrigger={renderTrigger}
      />
      {isManualAddPlayerEnabled ? (
        <ManuallyAddPlayerModal
          isOpen={isManualAddOpen}
          onClose={handleManualAddClose}
          searchText={searchText}
        />
      ) : null}
    </>
  );
};

export default HeaderSearchButtonContainer;
